import { useRef, useState } from 'react';
import api from '../lib/api';
import { logoUrl } from '../lib/useBrandLogos';
import { Button } from './ui/button';
import { Upload, Trash2, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

export const LogoUploader = ({ kind, title, hint, info, fallback, dark, onChange }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const src = logoUrl(kind, info?.updated_at) || fallback;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) { toast.error('Please choose a PNG, JPG or WEBP image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Logo must be under 5 MB'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/settings/logo/${kind}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(data[`${kind}_logo`]);
      toast.success('Logo uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Logo upload failed');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm('Remove this logo? The default logo will be shown instead.')) return;
    setBusy(true);
    try {
      await api.delete(`/settings/logo/${kind}`);
      onChange(null);
      toast.success('Logo removed');
    } catch { toast.error('Failed to remove logo'); } finally { setBusy(false); }
  };

  return (
    <div className="border border-[#E6EDE6] rounded-sm p-5 flex flex-col sm:flex-row gap-5 items-start" data-testid={`logo-uploader-${kind}`}>
      <div className={`w-28 h-28 shrink-0 rounded-sm flex items-center justify-center border border-[#E6EDE6] ${dark ? 'bg-[#1B2A1B]' : 'bg-[#F4F7F4]'}`}>
        {src ? <img src={src} alt={title} className="max-w-[88%] max-h-[88%] object-contain" data-testid={`logo-preview-${kind}`} />
          : <ImageIcon className="text-olive-400" size={28} />}
      </div>
      <div className="flex-1 space-y-2">
        <p className="font-medium text-[#213021]">{title}</p>
        <p className="text-xs text-olive-500">{hint}</p>
        <p className="text-xs text-olive-400" data-testid={`logo-status-${kind}`}>
          {info ? `Custom logo: ${info.filename || 'uploaded'}` : 'Using default logo'}
        </p>
        <div className="flex gap-2 pt-1">
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} data-testid={`logo-file-input-${kind}`} />
          <Button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="btn-primary gap-2" data-testid={`logo-upload-btn-${kind}`}>
            <Upload size={14} /> {busy ? 'Working...' : info ? 'Replace Logo' : 'Upload Logo'}
          </Button>
          {info && (
            <Button type="button" variant="outline" onClick={remove} disabled={busy} className="gap-2 text-red-600" data-testid={`logo-remove-btn-${kind}`}>
              <Trash2 size={14} /> Remove
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
